import React from "react";
import { Flex, useScreenSize } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { JSQ_SUPPORT_EMAIL } from "common/constants/copy";
import { type dgEventGlobalThis } from "common/utils/o11y/types";
import classNames from "classnames";
import jsqLogo from "./jsq_small_grey_logo.svg";
import styles from "./styles.scss";

const runtimeGlobal = globalThis as dgEventGlobalThis;

type SupportContentFooterProps = {
  arenaName?: string;
  arenaContactEmail?: string | null;
  arenaContactPhone?: string | null;
  websiteUrl?: string | null;
  showPoweredBy?: boolean;
  showArenaName?: boolean;
  hideCopyrightText?: boolean;
};

const defaultProps = {
  showPoweredBy: true,
  showArenaName: true,
};

export const SupportContentFooter = (props: SupportContentFooterProps) => {
  const screenSize = useScreenSize();
  const hideCopyrightFromFlag = useFeatureFlag("hideCopyrightText", false);
  const hideCopyrightText =
    props.hideCopyrightText !== undefined ? props.hideCopyrightText : hideCopyrightFromFlag;

  const lastLine = props.arenaName && props.showPoweredBy;

  const onCookieSettingsClick = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    runtimeGlobal.DG_BANNER_API?.showConsentBanner();
  };
  return (
    <Flex flexDirection="column" className={styles.SupportContentFooter__wrapper}>
      {props.showArenaName && (
        <Flex className="stacked-sm" alignContent="center" justifyContent="center">
          <p className="text-lg secondary strong" data-cy="arena-name">
            {props.websiteUrl ? (
              <a href={props.websiteUrl} data-cy="arena-link">
                {props.arenaName}
              </a>
            ) : (
              props.arenaName || "Juniper Square"
            )}
          </p>
        </Flex>
      )}
      <Flex alignContent="center" justifyContent="center">
        {props.arenaContactEmail || props.arenaContactPhone || props.arenaName ? (
          <Flex
            className="text-md secondary"
            data-cy="arena-email"
            justifyContent="center"
            flexDirection={screenSize === "small" ? "column" : "row"}
            alignItems="center"
          >
            {props.arenaContactEmail && (
              <a
                className="underline secondary link"
                href={`mailto:${props.arenaContactEmail}`}
              >
                {props.arenaContactEmail}
              </a>
            )}
            {screenSize !== "small" && props.arenaContactEmail && props.arenaContactPhone && (
              <>&nbsp;&middot;&nbsp;</>
            )}
            {props.arenaContactPhone && (
              <a
                className="secondary"
                href={`tel:${props.arenaContactPhone}`}
                data-cy="arena-phone"
              >
                {props.arenaContactPhone}
              </a>
            )}
          </Flex>
        ) : (
          <p className="text-md secondary">
            <a className="underline secondary link" href={`mailto:${JSQ_SUPPORT_EMAIL}`}>
              {JSQ_SUPPORT_EMAIL}
            </a>
          </p>
        )}
      </Flex>
      <Flex
        className={lastLine ? "stacked-xl" : undefined}
        alignContent="center"
        justifyContent="center"
      >
        <p className="text-md secondary" data-cy="arena-copyright">
          {!hideCopyrightText && (
            <>
              © {new Date().getFullYear()} {props.arenaName || "Juniper Square, Inc."} |{" "}
            </>
          )}
          <a
            className={classNames(
              styles.SupportContentFooter__PrivacyPolicyFontWeight,
              "secondary",
            )}
            href="https://www.junipersquare.com/terms-and-policies/privacy-policy"
            target="_blank"
            rel="noreferrer"
          >
            Privacy Policy
          </a>{" "}
          |{" "}
          <a
            className={classNames(
              styles.SupportContentFooter__PrivacyPolicyFontWeight,
              "secondary",
            )}
            onClick={onCookieSettingsClick}
            href="/"
          >
            Cookie Settings
          </a>
        </p>
      </Flex>
      {lastLine && (
        <Flex alignContent="center" justifyContent="center">
          <p className="text-md strong secondary">
            <img className="inset-sm" alt="Juniper Square Logo" src={jsqLogo} /> Powered by
            Juniper Square
          </p>
        </Flex>
      )}
    </Flex>
  );
};

SupportContentFooter.defaultProps = defaultProps;
export default SupportContentFooter;
